import type { ApiResponse } from "@nelurio/contracts";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";
const CSRF_KEY = "nelurio.csrf";

export class ApiClientError extends Error {
  constructor(public status: number, public code: string, message: string, public fields?: Record<string, string>) {
    super(message);
  }
}

export function isDemoMode() {
  return DEMO_MODE;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = window.localStorage.getItem(CSRF_KEY);
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  // GET-only auto-retry: bridges the 1-3s API restart window (connection
  // refused = fetch rejection, or 502/503/504) transparently for every read
  // surface. Writes are NEVER retried — a blind POST retry would duplicate
  // creations (no idempotency keys).
  const safe = ["GET", "HEAD", "OPTIONS"].includes(method);
  const doFetch = () => fetch(`${API_BASE_URL}${path}`, { ...options, headers, credentials: "include" });
  const run = async (): Promise<{ response: Response | null; error: unknown }> => {
    try {
      return { response: await doFetch(), error: null };
    } catch (error) {
      return { response: null, error };
    }
  };
  let result = await run();
  if (safe) {
    for (const backoff of [400, 800]) {
      const failed = result.response === null;
      const retryable = result.response !== null && (result.response.status === 502 || result.response.status === 503 || result.response.status === 504);
      if ((!failed && !retryable) || options.signal?.aborted) break;
      await new Promise((resolve) => setTimeout(resolve, backoff));
      result = await run();
    }
  }
  if (!result.response) throw result.error ?? new Error("network_error");
  const response = result.response;

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok) {
    const error = payload?.error;
    throw new ApiClientError(response.status, error?.code || "request_failed", error?.message || response.statusText, error?.fields);
  }
  const csrf = response.headers.get("x-csrf-token") || payload?.meta?.csrfToken;
  if (csrf) window.localStorage.setItem(CSRF_KEY, csrf);
  return (payload as ApiResponse<T>)?.data ?? payload;
}

export function clearCsrf() {
  window.localStorage.removeItem(CSRF_KEY);
}

/** Streaming POST for the AI chat: calls onDelta(fullTextSoFar, delta) per
 *  server-sent event and resolves with the final payload ({threadId,text,...}). */
export async function apiStream<T>(path: string, body: unknown, onDelta: (full: string, delta: string) => void): Promise<T> {
  const csrf = window.localStorage.getItem(CSRF_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
    credentials: "include",
    body: JSON.stringify(body)
  });
  if (!response.ok || !response.body) {
    const payload = response.headers.get("content-type")?.includes("application/json") ? await response.json().catch(() => null) : null;
    const error = payload?.error;
    throw new ApiClientError(response.status, error?.code || "request_failed", error?.message || response.statusText);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";
  let final: T | null = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let index;
    while ((index = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, index);
      buffer = buffer.slice(index + 2);
      const line = chunk.split("\n").find((candidate) => candidate.startsWith("data: "));
      if (!line) continue;
      const event = JSON.parse(line.slice(6));
      if (event.t === "d") { full += event.v; onDelta(full, event.v); }
      else if (event.t === "done") { final = event as T; }
      else if (event.t === "err") { throw new ApiClientError(502, event.code || "ai_upstream", event.message || "AI service hiccup."); }
    }
  }
  if (!final) throw new ApiClientError(502, "ai_upstream", "Stream ended unexpectedly.");
  return final;
}
